import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3 } from "lucide-react";

import {
  orgActiveCommunitiesActionLabel,
  orgAttendanceActionLabel,
  communitiesOverviewActionLabel,
  formatEventsPerCommunityValue,
  formatRate,
  formatRateDeltaPpBadge,
  formatSignedCountPairDelta,
  formatSignedEpcBadgeDelta,
  GrowthBars,
  orgActiveCommunitiesStateLabel,
  orgAttendanceStateLabel,
  orgCommunitiesStateLabel,
  orgEventsPerCommunity30dStateLabel,
  orgEventsPerCommunityActionLabel,
  orgMembersStateLabel,
  orgMetricStatusLine,
  orgMembersActionLabel,
  orgParticipationActionLabel,
  orgParticipationStateLabel,
  OverviewMetricCard,
  trendDirectionFromCounts,
  trendDirectionFromRates,
  trendWordFromCounts,
  trendWordFromRates,
} from "@/components/analytics/analytics-widgets";
import { AppPageContent } from "@/components/layout/app-page";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/section-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGroupLabel } from "@/hooks/use-group-label";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/analytics")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const groupLabel = useGroupLabel();
  const glWords = { singular: groupLabel.singular, plural: groupLabel.plural };
  const { data: currentUser } = useCurrentUser();

  const isCompanyAdmin = currentUser?.role === "company_admin";

  const { data, isPending } = useQuery({
    ...convexQuery(api.analytics.getOrgAnalytics, {}),
    enabled: isCompanyAdmin === true,
  });

  if (!currentUser) return null;

  if (!isCompanyAdmin) {
    return (
      <AppPageContent variant="medium">
        <div className="flex w-full max-w-lg flex-col gap-4 py-6">
          <BarChart3 className="h-12 w-12 text-muted-foreground/60" aria-hidden />
          <h1 className="text-xl font-semibold text-foreground">Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Organisation analytics are available to company administrators only.
          </p>
          <Button render={<Link to="/app" />} nativeButton={false}>
            Back to overview
          </Button>
        </div>
      </AppPageContent>
    );
  }

  if (isPending || data === undefined) {
    return (
      <AppPageContent variant="medium" className="space-y-6">
        <Skeleton className="h-10 w-72 rounded-lg" />
        <Skeleton className="h-4 max-w-xl rounded-md" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="min-h-[220px] rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </AppPageContent>
    );
  }

  if (data === null) {
    return (
      <AppPageContent variant="medium">
        <div className="flex w-full max-w-lg flex-col gap-4 py-6">
          <BarChart3 className="h-12 w-12 text-muted-foreground/60" aria-hidden />
          <p className="text-sm text-muted-foreground">You do not have access to this data.</p>
          <Button render={<Link to="/app" />} nativeButton={false}>
            Back to overview
          </Button>
        </div>
      </AppPageContent>
    );
  }

  const glCap = groupLabel.plural.charAt(0).toUpperCase() + groupLabel.plural.slice(1);
  const glSingularLower =
    groupLabel.singular === "ERG"
      ? "ERG"
      : groupLabel.singular.charAt(0).toLowerCase() + groupLabel.singular.slice(1);

  const participationNoData = data.companyUsersCount === 0;
  const participationDisplay = participationNoData ? "—" : formatRate(data.participationRate);

  const attendanceNoData =
    data.pastEventsCount === 0 ||
    data.attendanceRate === null ||
    data.attendanceRate === undefined ||
    !Number.isFinite(data.attendanceRate);
  const attendanceValueDisplay = attendanceNoData ? "—" : formatRate(data.attendanceRate);

  const activeCommunitiesValueDisplay =
    data.communitiesCount === 0 ||
    data.activeCommunitiesRateLast30 === null ||
    data.activeCommunitiesRateLast30 === undefined
      ? "—"
      : formatRate(data.activeCommunitiesRateLast30);

  const epcLast30 = data.communitiesCount > 0 ? data.eventsLast30Days / data.communitiesCount : 0;
  const epcPrev30 =
    data.communitiesCount > 0 ? data.eventsPrevious30Days / data.communitiesCount : 0;
  const eventsPerCommunityDisplay =
    data.communitiesCount === 0 ? "—" : formatEventsPerCommunityValue(epcLast30);

  return (
    <AppPageContent variant="medium" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Analytics</h1>
        <p className="mt-1.5 text-muted-foreground">Key metrics and trends from the last 30 days</p>
      </div>

      <SectionCard
        title="Overview"
        description="Activity and trends from the last 30 days"
        contentClassName="pt-2"
      >
        <div className="grid grid-cols-1 gap-4 *:min-w-0 sm:grid-cols-2 lg:grid-cols-3">
          <OverviewMetricCard
            label={glCap}
            value={String(data.communitiesCount)}
            status={orgMetricStatusLine(
              orgCommunitiesStateLabel(data.communitiesCount),
              trendWordFromCounts(
                data.communitiesCreatedLast30Days,
                data.communitiesCreatedPrevious30Days,
              ),
            )}
            trendDirection={trendDirectionFromCounts(
              data.communitiesCreatedLast30Days,
              data.communitiesCreatedPrevious30Days,
            )}
            trendLabel={formatSignedCountPairDelta(
              data.communitiesCreatedLast30Days,
              data.communitiesCreatedPrevious30Days,
            )}
            actionLabel={communitiesOverviewActionLabel(data.communitiesCount, glWords)}
            actionLink={
              <Link to={data.communitiesCount < 5 ? "/app/groups/new" : "/app/admin/communities"} />
            }
          />
          <OverviewMetricCard
            label="Members"
            value={String(data.communityMembershipsCount)}
            status={orgMetricStatusLine(
              orgMembersStateLabel(data.communityMembershipsCount),
              trendWordFromCounts(data.membersJoinedLast30Days, data.membersJoinedPrevious30Days),
            )}
            trendDirection={trendDirectionFromCounts(
              data.membersJoinedLast30Days,
              data.membersJoinedPrevious30Days,
            )}
            trendLabel={formatSignedCountPairDelta(
              data.membersJoinedLast30Days,
              data.membersJoinedPrevious30Days,
            )}
            actionLabel={orgMembersActionLabel(data.communityMembershipsCount)}
            actionLink={<Link to="/app/admin/roles" />}
          />
          <OverviewMetricCard
            label="Participation"
            labelTooltip="Percentage of users that joined at least one community"
            value={participationDisplay}
            status={orgMetricStatusLine(
              orgParticipationStateLabel(data.participationRate),
              trendWordFromRates(null, null),
            )}
            trendDirection={trendDirectionFromRates(null, null)}
            trendLabel={formatRateDeltaPpBadge(null, null)}
            actionLabel={orgParticipationActionLabel(data.participationRate, glWords)}
            actionLink={
              data.participationRate !== null &&
              data.participationRate !== undefined &&
              data.participationRate * 100 >= 60 ? (
                <Link to="/app/admin/communities" />
              ) : (
                <Link to="/app/admin/roles" />
              )
            }
          />
          <OverviewMetricCard
            label={`Events per ${groupLabel.singular}`}
            value={eventsPerCommunityDisplay}
            status={orgMetricStatusLine(
              orgEventsPerCommunity30dStateLabel(data.eventsLast30Days, data.communitiesCount),
              trendWordFromCounts(Math.round(epcLast30 * 100), Math.round(epcPrev30 * 100)),
            )}
            trendDirection={trendDirectionFromCounts(
              Math.round(epcLast30 * 100),
              Math.round(epcPrev30 * 100),
            )}
            trendLabel={formatSignedEpcBadgeDelta(epcLast30, epcPrev30)}
            actionLabel={orgEventsPerCommunityActionLabel(
              data.eventsLast30Days,
              data.communitiesCount,
            )}
            actionLink={
              epcLast30 < 1 ? (
                <Link to="/app/admin/communities" search={{ filter: "low-activity" }} />
              ) : (
                <Link to="/app/events" />
              )
            }
          />
          <OverviewMetricCard
            label="Attendance"
            labelTooltip="Percentage of invitees that attended the event"
            value={attendanceValueDisplay}
            status={orgMetricStatusLine(
              orgAttendanceStateLabel(data.attendanceRate),
              trendWordFromRates(data.attendanceRateLast30Days, data.attendanceRatePrevious30Days),
            )}
            trendDirection={trendDirectionFromRates(
              data.attendanceRateLast30Days,
              data.attendanceRatePrevious30Days,
            )}
            trendLabel={formatRateDeltaPpBadge(
              data.attendanceRateLast30Days,
              data.attendanceRatePrevious30Days,
            )}
            actionLabel={orgAttendanceActionLabel(attendanceNoData, data.attendanceRate)}
            actionLink={
              attendanceNoData ? (
                <Link to="/app/events/create" />
              ) : data.attendanceRate !== null &&
                data.attendanceRate !== undefined &&
                Number.isFinite(data.attendanceRate) &&
                data.attendanceRate * 100 < 50 ? (
                <Link to="/app/admin/communities" search={{ filter: "low-attendance" }} />
              ) : (
                <Link to="/app/events" />
              )
            }
          />
          <OverviewMetricCard
            label={`Active ${groupLabel.plural}`}
            labelTooltip="Percentage of communities with at least one event in the last 30 days"
            value={activeCommunitiesValueDisplay}
            status={orgMetricStatusLine(
              orgActiveCommunitiesStateLabel(data.activeCommunitiesRateLast30),
              trendWordFromRates(
                data.activeCommunitiesRateLast30,
                data.activeCommunitiesRatePrevious30,
              ),
            )}
            trendDirection={trendDirectionFromRates(
              data.activeCommunitiesRateLast30,
              data.activeCommunitiesRatePrevious30,
            )}
            trendLabel={formatRateDeltaPpBadge(
              data.activeCommunitiesRateLast30,
              data.activeCommunitiesRatePrevious30,
            )}
            actionLabel={orgActiveCommunitiesActionLabel(data.activeCommunitiesRateLast30, glWords)}
            actionLink={
              data.activeCommunitiesRateLast30 !== null &&
              data.activeCommunitiesRateLast30 !== undefined &&
              Number.isFinite(data.activeCommunitiesRateLast30) &&
              data.activeCommunitiesRateLast30 * 100 >= 70 ? (
                <Link to="/app/admin/communities" />
              ) : (
                <Link to="/app/admin/communities" search={{ filter: "inactive" }} />
              )
            }
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Activity"
        description={`Activity (${glSingularLower} membership) over the last 12 months.`}
        contentClassName="space-y-0"
      >
        <GrowthBars title="Members by month" series={data.membersTotalByMonth} />
        <div className="mt-6 border-t border-border pt-6">
          <GrowthBars title="Events by month" series={data.eventGrowth} />
        </div>
      </SectionCard>
    </AppPageContent>
  );
}
